/**
 * Special Folders Utility
 * Handles detection of special folder types (synced folders and read-only folders)
 * and provides helper functions for determining folder permissions.
 */

import { Datum } from "./datastructure";

export type SpecialFolderType =
  | "ms-teams"
  | "ms-sharepoint"
  | "ms-onenote"
  | "box"
  | "google-drive"
  | "dropbox"
  | "read-only-drive"
  | null;

/**
 * The root folder the backend synthesizes for files awaiting automatic
 * placement (#11257). Must match `PROCESSING_FOLDER_NAME` in
 * `fp-backend/fp/reserved_folders.py` and the literal in
 * `postgres/queries.blobs.sql`.
 */
export const PROCESSING_FOLDER_NAME = "Processing";

/**
 * Context that scopes read-only-drive detection. Every field is optional so
 * non-enforcing call sites (icons in contexts without a drives listing, tests)
 * keep working; when the name lists are omitted, no folder is treated as a
 * read-only client drive.
 *
 * The names come from the drives listing, resolved per-org by the backend
 * (#10972) — which drives are read-only, and which of those are scoped to a
 * particular client, is deliberately not knowable from the frontend bundle.
 */
export interface ReadOnlyContext {
  /**
   * Lowercased names of the current org's read-only client drives
   * (`drives_listing.read_only_drive_names`).
   */
  readOnlyDriveNames?: readonly string[];
  /**
   * Lowercased names of the SFTP-backed subset, re-syncable on demand
   * (`drives_listing.sftp_drive_names`, #10860).
   */
  sftpDriveNames?: readonly string[];
  /**
   * Drive name -> the connector backing it, so a read-only drive can show that
   * vendor's icon (`drive_connectors` on the drive policy).
   */
  driveConnectors?: Record<string, string>;
  /**
   * Lowercased names of the current org's named SharePoint site roots
   * (`drive_policy.sharepoint_root_names`, #11713). Each connected site syncs
   * into its own top-level folder named after the site; these folders (and
   * their subtrees) get the SharePoint icon and read-only treatment exactly
   * like the legacy shared `Sharepoint` root.
   */
  sharepointRootNames?: readonly string[];
  /** Across-drives mode — path[0] is a synthetic drive id, so the drive root is path[1]. */
  isAcrossDrivesMode?: boolean;
}

/**
 * Whether a folder name is one of this org's read-only client drives.
 *
 * Callers that have already lowercased the name can pass it as-is.
 */
export function readOnlyDriveNameApplies(
  name: string,
  readOnlyDriveNames?: readonly string[]
): boolean {
  if (!readOnlyDriveNames) return false;
  return readOnlyDriveNames.includes(name.toLowerCase());
}

/**
 * The path index of the drive root. In across-drives mode path[0] is a
 * synthetic drive id, so the real drive root is path[1]; otherwise it's path[0].
 */
function rootIndex(ctx?: ReadOnlyContext): number {
  return ctx?.isAcrossDrivesMode ? 1 : 0;
}

/**
 * Check if a path segment matches an externally-synced special folder type.
 * Read-only client drives are handled separately (root-anchored + org-scoped).
 */
function checkSyncedSegment(segment: string): SpecialFolderType {
  const lower = segment.toLowerCase();
  if (lower === "onenote") return "ms-onenote";
  if (lower === "sharepoint") return "ms-sharepoint";
  if (lower === "teams channels" || lower === "channels") return "ms-teams";
  if (lower === "box") return "box";
  if (lower === "google drive") return "google-drive";
  if (lower === "dropbox") return "dropbox";
  return null;
}

/**
 * Detect special folder type from a path.
 *
 * Read-only client drives are matched ONLY at the drive root (path[0], or
 * path[1] in across-drives mode) so an unrelated nested folder that merely
 * shares the name is not treated as read-only (#10020). Which names those are
 * is per-org and comes from the drives listing.
 *
 * Synced folders (SharePoint / Teams / OneNote / Box) are likewise matched
 * ONLY at the drive root so an unrelated nested folder that shares a
 * connector's name — e.g. `Companies/Box` — is not treated as the connector
 * (#10765). Descendants of a real connector root stay classified because the
 * root segment is unchanged down the subtree.
 */
export function getSpecialFolderType(
  path: string[],
  ctx?: ReadOnlyContext
): SpecialFolderType {
  if (!path[0]) return null;

  // Both special-folder kinds are anchored to the drive-root segment: path[0],
  // or path[1] in across-drives mode where path[0] is a synthetic drive id.
  const rootSeg = path[rootIndex(ctx)];
  if (!rootSeg) return null;

  // Read-only client drive: scoped by the names this org was served.
  if (readOnlyDriveNameApplies(rootSeg, ctx?.readOnlyDriveNames)) {
    return "read-only-drive";
  }

  // Named SharePoint site root: per-org names served by the backend (#11713),
  // root-anchored like everything else here.
  if ((ctx?.sharepointRootNames ?? []).includes(rootSeg.toLowerCase())) {
    return "ms-sharepoint";
  }

  // Externally-synced connector folder.
  return checkSyncedSegment(rootSeg);
}

/**
 * Whether a datum is the synthetic Processing folder itself (#11257).
 *
 * Exact root path only, and only a folder. Nothing stores this path, so the
 * folder has no rows behind it and every folder-level mutation would resolve to
 * the empty set; the backend rejects those, and the tree hides them. A folder
 * deeper in the tree that shares the name is somebody's real folder, and a file
 * that happens to be named Processing is a file — neither is synthesized.
 *
 * Its *contents* are deliberately not covered. They are the same files that sat
 * at the root before this change, where they could be dragged and deleted;
 * grouping them for display should not quietly remove that.
 */
export function isProcessingFolder(
  datum: Datum,
  ctx?: ReadOnlyContext
): boolean {
  if (datum.type !== "folder") return false;
  const idx = rootIndex(ctx);
  if (datum.path.length !== idx + 1) return false;
  const name = datum.path[idx];
  return name?.toLowerCase() === PROCESSING_FOLDER_NAME.toLowerCase();
}

/**
 * Check if a datum is in a read-only special folder (synced, a read-only drive,
 * or the synthetic Processing folder)
 */
export function isReadOnlyDatum(datum: Datum, ctx?: ReadOnlyContext): boolean {
  if (isProcessingFolder(datum, ctx)) return true;
  return getSpecialFolderType(datum.path, ctx) !== null;
}

/**
 * Check if datum is inside a synced folder whose connector can re-sync a
 * folder on demand (for the Sync action's applicability). These are the
 * connectors with a folder-scoped sync endpoint: OneNote, SharePoint, Teams,
 * Box. Google Drive and Dropbox sync whole connections from the Connectors
 * page, so their subtrees are read-only (see `isReadOnlyDatum`) but offer no
 * folder Sync — listing them here made the menu item resolve to "not a synced
 * folder" (#11957).
 */
export function isInSyncedFolder(datum: Datum, ctx?: ReadOnlyContext): boolean {
  const type = getSpecialFolderType(datum.path, ctx);
  return (
    type === "ms-onenote" ||
    type === "ms-sharepoint" ||
    type === "ms-teams" ||
    type === "box"
  );
}

/**
 * Whether a datum is the root folder of an SFTP-backed shared drive, and so can
 * be re-synced on demand (#10860).
 *
 * Root-only, deliberately: SFTP sync is connection-scoped (the syncer refuses a
 * partial sync), so a "Sync" offered on a subfolder would imply a scoping that
 * does not exist. This is intentionally separate from `isInSyncedFolder` —
 * routing these drives through `getSpecialFolderType` as a synced type would
 * also drop their read-only classification and re-enable upload/rename/delete.
 */
export function isSftpSyncableDriveRoot(
  datum: Datum,
  ctx?: ReadOnlyContext
): boolean {
  if (datum.type !== "folder") return false;
  const idx = rootIndex(ctx);
  // Must be the drive root itself, never a descendant.
  if (datum.path.length !== idx + 1) return false;
  const name = datum.path[idx];
  return (
    name !== undefined &&
    (ctx?.sftpDriveNames ?? []).includes(name.toLowerCase())
  );
}

/**
 * Check if a datum is itself a read-only client-drive root folder.
 * Drives the custom icon / tooltip and pin-to-top, so it must be the drive
 * root itself — not a descendant, and not a same-named nested folder (#10020).
 */
export function isReadOnlyDriveFolder(
  datum: Datum,
  ctx?: ReadOnlyContext
): boolean {
  if (datum.type !== "folder") return false;
  const idx = rootIndex(ctx);
  // Must be the drive root itself: a top-level folder (or the first folder
  // under a synthetic drive id in across-drives mode), never a descendant.
  if (datum.path.length !== idx + 1) return false;
  const name = datum.path[idx];
  return (
    name !== undefined &&
    readOnlyDriveNameApplies(name, ctx?.readOnlyDriveNames)
  );
}
